"use client"

import { Button } from "@/components/ui/button"
import { useEmailAnalytics } from "@/hooks/use-email-analytics"
import AnalysisDashboardHeader from "@/components/analysisPage/AnalysisDashboardHeader"
import { TagLoaders } from "@/components/TagLoaders"
import {
  BarChart3,
  MousePointer2,
  Eye,
  Ban,
  CheckCircle2,
  Users,
  UserCheck,
  UserMinus
} from "lucide-react"

// Modern Components from the new directory
import AnalysisFunnel from "@/components/dashboard/analysis/AnalysisFunnel"
import MetricSparkCard from "@/components/dashboard/analysis/MetricSparkCard"
import GrowthChart from "@/components/dashboard/analysis/GrowthChart"
import RecentActivityFeed from "@/components/dashboard/analysis/RecentActivityFeed"
import ModernCampaignTable from "@/components/dashboard/analysis/ModernCampaignTable"

/**
 * AnalysisPage component — Modern Redesign
 * 
 * Features a high-fidelity dashboard layout with:
 * - Linear performance funnel (Hero section)
 * - Glassmorphic stat cards with trends
 * - Interactive contact growth chart (Recharts)
 * - Live activity feed
 * - High-density campaign performance breakdown
 */
export default function AnalysisPage() {
  const {
    analytics,
    timeRange,
    isLoading,
    error,
    setTimeRange,
    refetch,
    campaignWithStats,
    isCampaignWithStatsLoading,
    refetchCampaignsWithStats,
  } = useEmailAnalytics("today");

  // Loading State with custom TagLoaders
  if (isLoading || isCampaignWithStatsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <TagLoaders
          tags={["Analytics"]}
          message="Preparing your performance dashboard..."
          variant="bars"
        />
      </div>
    )
  }

  // Error State or Empty Data
  if (error || !analytics) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] text-center gap-4">
        <div className="p-4 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-500">
          <Ban size={32} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Data Fetch Failed</h2>
          <p className="text-slate-500 text-sm mt-1">We couldn&apos;t retrieve the latest performance stats.</p>
        </div>
        <Button
          onClick={async () => {
            await refetch();
            await refetchCampaignsWithStats();
          }}
          variant="outline"
          className="rounded-xl border border-white/10 hover:bg-white/5 active:scale-95 transition-all"
        >
          Try Again
        </Button>
      </div>
    )
  }

  const { campaignStats } = analytics;

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto min-h-screen bg-slate-950 text-slate-200">

      {/* 🚀 Header & Global Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-900/50 p-6 rounded-3xl border border-white/5 backdrop-blur-xl shadow-2xl">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Analytics</h1>
          <p className="text-sm text-slate-500 font-medium mt-1 uppercase tracking-widest">Real-time engagement & reach insights</p>
        </div>
        <AnalysisDashboardHeader timeRange={timeRange} setTimeRange={setTimeRange} />
      </div>

      {/* 📊 Main Stats Visualizations */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

        {/* Funnel: Hero Section (2/3 width on desktop) */}
        <div className="xl:col-span-8">
          <AnalysisFunnel stats={{
            sent: campaignStats.sent,
            delivered: campaignStats.delivered,
            opened: campaignStats.opened,
            clicked: campaignStats.clicked,
            deliveryRate: campaignStats.deliveryRate,
            openRate: campaignStats.openRate,
            clickRate: campaignStats.clickRate
          }} />
        </div>

        {/* Essential Rates Grid (1/3 width on desktop) */}
        <div className="xl:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MetricSparkCard
            label="Open Rate"
            value={`${campaignStats.openRate}%`}
            subValue={`${campaignStats.opened.toLocaleString()} Unique`}
            icon={Eye}
            colorClass="text-purple-400"
            glowColor="bg-purple-500/20"
            index={0}
          />
          <MetricSparkCard
            label="Click Rate"
            value={`${campaignStats.clickRate}%`}
            subValue={`${campaignStats.clicked.toLocaleString()} Clicks`}
            icon={MousePointer2}
            colorClass="text-emerald-400"
            glowColor="bg-emerald-500/20"
            index={1}
          />
          <MetricSparkCard
            label="Bounce Rate"
            value={`${campaignStats.bounceRate}%`}
            subValue={`${campaignStats.bounced.toLocaleString()} Bounced`}
            icon={Ban}
            colorClass="text-rose-400"
            glowColor="bg-rose-500/20"
            index={2}
          />
          <MetricSparkCard
            label="Delivery"
            value={`${campaignStats.deliveryRate}%`}
            subValue={`${campaignStats.delivered.toLocaleString()} Inbox`}
            icon={CheckCircle2}
            colorClass="text-blue-400"
            glowColor="bg-blue-500/20"
            index={3}
          />
        </div>
      </div>

      {/* 📈 Middle Row: Growth & Population Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Contact Growth Area Chart */}
        <div className="lg:col-span-8 h-full min-h-[400px]">
          <GrowthChart data={analytics.contactGrowth || []} />
        </div>

        {/* Audience Breakdown Sidebar */}
        <div className="lg:col-span-4 grid grid-cols-1 gap-4">

          {/* Total Database Card */}
          <div className="p-8 rounded-3xl border border-white/10 bg-indigo-600/10 backdrop-blur-xl relative overflow-hidden group min-h-[160px] flex flex-col justify-center">
            <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 group-hover:opacity-20 transition-all duration-700">
              <Users size={180} className="text-indigo-400" />
            </div>
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.3em] mb-3 block">Total Database Size</span>
            <div className="flex items-end gap-3">
              <h2 className="text-5xl font-black text-white tracking-tighter">{analytics.totalContacts.toLocaleString()}</h2>
              <span className="text-xs text-slate-500 mb-2 font-bold uppercase tracking-widest">Contacts</span>
            </div>
          </div>

          {/* Active vs Inactive Split */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-5 rounded-3xl border border-emerald-500/20 bg-emerald-500/5 backdrop-blur-md group hover:bg-emerald-500/10 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-emerald-500/10">
                  <UserCheck size={14} className="text-emerald-400" />
                </div>
                <span className="text-[10px] font-bold text-emerald-500/70 uppercase tracking-widest">Active</span>
              </div>
              <div className="text-3xl font-bold text-white tracking-tight">{analytics.activeContacts.toLocaleString()}</div>
            </div>
            <div className="p-5 rounded-3xl border border-rose-500/20 bg-rose-500/5 backdrop-blur-md group hover:bg-rose-500/10 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-rose-500/10">
                  <UserMinus size={14} className="text-rose-400" />
                </div>
                <span className="text-[10px] font-bold text-rose-500/70 uppercase tracking-widest">Inactive</span>
              </div>
              <div className="text-3xl font-bold text-white tracking-tight">{analytics.inactiveContacts.toLocaleString()}</div>
            </div>
          </div>

          {/* Campaigns Statistics */}
          <div className="p-6 rounded-3xl border border-white/5 bg-slate-900/40 flex items-center justify-between group hover:border-white/10 transition-all">
            <div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Total Campaigns Ran</span>
              <span className="text-2xl font-black text-white tracking-tight">{analytics.totalCampaigns}</span>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center group-hover:bg-indigo-500/10 group-hover:border-indigo-500/20 transition-all">
              <BarChart3 size={20} className="text-slate-400 group-hover:text-indigo-400" />
            </div>
          </div>
        </div>
      </div>

      {/* 📅 Bottom Row: Campaign Breakdown & Audit Trail */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-12">
        {/* Performance Table (2/3 width) */}
        <div className="lg:col-span-8">
          <ModernCampaignTable campaigns={campaignWithStats.map(c => ({
            name: c.campaignName,
            delivered: c.Delivered || 0,
            opened: c.Open || 0,
            clicked: c.Click || 0,
            bounced: c.Bounce || 0
          }))} />
        </div>

        {/* Live Activity Feed (1/3 width) */}
        <div className="lg:col-span-4">
          <RecentActivityFeed activities={analytics.recentActivity || []} />
        </div>
      </div>
    </div>
  )
}
